/* eslint-disable camelcase */
/* eslint-disable no-unused-expressions */
import React, { Fragment, useContext } from 'react';
import Close from '@material-ui/icons/Close';
import Check from '@material-ui/icons/Check';
import { IconButton, Tooltip } from '@material-ui/core';
import {
  generateNewFrequency,
  getFreqText,
} from '@mummssoftware/utils/frequencies/frequency-utils';
import { useTheme } from '@material-ui/core/styles';
import {
  VFGlobalContext,
  CLEAR_EDITING_DATA,
  REMOVE_EDITING_ROLEGROUP,
} from '@mummssoftware/global-context/VFGlobalContext';
import { CPFContext } from '../../containers/Context/CPFContext';
import {
  SET_EDITING_ROLEGROUP,
  RESET_EDITING_ROLEGROUP,
  SET_API_ERROR,
} from '../../containers/Context/constants';
import { CarePlanFrequencyContext } from '../../context';
import {
  hasEndDate,
  clearCarePlanEdits,
  snapshotCarePlanDisplay,
  restoreCarePlanDisplay,
} from './utils';
import { prepareInlineSaveEdits } from './cellEditState';
import {
  getWidgetEditMode,
  getInlineSaveVia,
  CPF_SAVE_VIA,
} from '../../editPolicy';
import defaults from '../../translations/en.json';
import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';

export const OptionsCell = (props: any) => {
  const { data, gridOptions, rgHash } = props;
  const { roleGroupId, id } = data;

  const { patient, agency, postCarePlan } = useContext(
    CarePlanFrequencyContext,
  );
  const {
    dispatch,
    editingRoleGroupId,
    isCPFAgency,
    isPRNOrderAgency,
  } = useContext(CPFContext);
  const { VFordersMode, dispatchVF, VForderData } = useContext(VFGlobalContext);

  const carePlan: mumms.ProcessedCarePlan =
    rgHash[roleGroupId.toString()].frequencies[id] ||
    rgHash[roleGroupId.toString()].frequencies.dummy;

  const mode = getWidgetEditMode({
    isCPFAgency: !!isCPFAgency,
    isPRNOrderAgency: !!isPRNOrderAgency,
    VFordersMode,
  });
  const inlineSave = getInlineSaveVia(mode) === CPF_SAVE_VIA.POST_CARE_PLAN;
  const thisRowEditing =
    editingRoleGroupId != null &&
    String(editingRoleGroupId) === String(roleGroupId);

  // Way 2: check/X while this row is locked. Order mode uses pencil column.
  const displayEdit = !hasEndDate(carePlan) && inlineSave && thisRowEditing;

  const {
    palette: { secondary, text },
  } = useTheme() as any;

  const borderlessButtonStyle = {
    outline: 'transparent',
    background: 'transparent',
    padding: '0px',
    color:
      (VForderData[roleGroupId] && VFordersMode && secondary.contrastText) ||
      text.primary,
  };

  const redrawRow = () => {
    if (gridOptions.api) {
      setTimeout(() => {
        gridOptions.api?.redrawRows();
      }, 0);
    }
  };

  const handleRowSave = async () => {
    if (hasEndDate(carePlan)) {
      return;
    }
    if (!inlineSave) {
      return;
    }
    // Days-only (or partial) edits: fill missing edit* from current plan so
    // postCarePlan validation can pass without showing Frequency/Effective.
    if (!prepareInlineSaveEdits(carePlan)) {
      dispatch({
        type: SET_API_ERROR,
        payload: {
          text: `There is an issue saving the frequency, please contact support`,
          severity: `error`,
        },
      });
      return;
    }
    if (!postCarePlan) {
      return;
    }

    const displaySnapshot = snapshotCarePlanDisplay(carePlan);

    dispatch({
      type: SET_API_ERROR,
      payload: {
        text: defaults.savingFrequency,
        severity: 'info',
      },
    });

    // Optimistic display so unlock/redraw does not flash old values.
    carePlan.period = String(carePlan.editPeriod);
    carePlan.frequency = carePlan.editFrequency as mumms.ProcessedCarePlan['frequency'];
    carePlan.frequencyText = getFreqText(carePlan);
    carePlan.daysOfWeek = carePlan.editDaysOfWeek || '';
    if (carePlan.editEffective) {
      carePlan.effectiveDate =
        carePlan.editEffective instanceof Date
          ? carePlan.editEffective.toISOString()
          : String(carePlan.editEffective);
    }

    dispatchVF({
      type: CLEAR_EDITING_DATA,
      payload: null,
    });
    dispatch({
      type: RESET_EDITING_ROLEGROUP,
      payload: null,
    });
    redrawRow();

    try {
      await postCarePlan(
        generateNewFrequency(rgHash, carePlan, patient, agency),
      );
      clearCarePlanEdits(carePlan);
      dispatch({
        type: SET_API_ERROR,
        payload: null,
      });
    } catch (e) {
      console.error(e);
      restoreCarePlanDisplay(carePlan, displaySnapshot);
      dispatch({
        type: SET_EDITING_ROLEGROUP,
        payload: roleGroupId,
      });
      redrawRow();
      dispatch({
        type: SET_API_ERROR,
        payload: {
          text: defaults.saveFrequencyFailed,
          severity: 'error',
        },
      });
    }
  };

  const handleRowEditClose = () => {
    clearCarePlanEdits(carePlan);
    dispatchVF({
      type: REMOVE_EDITING_ROLEGROUP,
      payload: roleGroupId.toString(),
    });
    dispatchVF({
      type: CLEAR_EDITING_DATA,
      payload: null,
    });
    dispatch({
      type: RESET_EDITING_ROLEGROUP,
      payload: null,
    });
    redrawRow();
  };

  return (
    <div>
      {displayEdit ? (
        <Fragment>
          {inlineSave && (
            <Tooltip title="Save">
              <IconButton
                onClick={handleRowSave}
                size="small"
                style={borderlessButtonStyle}
              >
                <Check />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Clear">
            <IconButton
              onClick={handleRowEditClose}
              size="small"
              style={borderlessButtonStyle}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Fragment>
      ) : null}
    </div>
  );
};
